import { useCallback, useEffect, useMemo, useState } from 'react';
import { Breadcrumb, Empty, List, Select, Space, Typography, theme } from 'antd';
import { CheckOutlined, FolderOutlined, FileTextOutlined, HomeOutlined } from '@ant-design/icons';

import { useLibraryTree } from '@/entities/training/api/libraryApi';
import {
  programKindLabels,
  programKindValues,
  recurrenceModelLabels,
  recurrenceModelValues,
  type LibraryTreeItem,
  type ProgramKind,
  type RecurrenceModel,
} from '@/entities/training/model/librarySchema';
import { SearchInput } from '@/shared/ui/SearchInput';
import { PageSpinner } from '@/shared/ui/PageSpinner';

import { ProgramDetailDrawer } from './ProgramDetailDrawer';
import s from './LibraryTab.module.scss';

const { Text } = Typography;

const SEARCH_DEBOUNCE_MS = 300;

interface BreadcrumbItem {
  id: string | null;
  name: string;
}

function joinMultiSelectDisplay(omittedValues: ReadonlyArray<{ label?: React.ReactNode; value?: string | number }>) {
  if (omittedValues.length === 0) return undefined;
  return omittedValues.map((v) => (typeof v.label === 'string' ? v.label : String(v.value ?? ''))).join(' · ');
}

export function LibraryTab() {
  const { token } = theme.useToken();
  const [rootId, setRootId] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [programKindFilter, setProgramKindFilter] = useState<string[]>([]);
  const [recurrenceFilter, setRecurrenceFilter] = useState<string[]>([]);
  const [levelBasedFilter, setLevelBasedFilter] = useState<boolean | undefined>(undefined);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([{ id: null, name: 'Библиотека' }]);
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const searchQuery = debouncedSearch.trim().length >= 2 ? debouncedSearch.trim() : undefined;

  const {
    data: items,
    isPending,
    isFetching,
  } = useLibraryTree({
    rootId: rootId,
    q: searchQuery,
    programKind: programKindFilter.length > 0 ? programKindFilter : undefined,
    recurrenceModel: recurrenceFilter.length > 0 ? recurrenceFilter : undefined,
    levelBased: levelBasedFilter,
  });

  const listAwaitingFirstPaint = isPending && items === undefined;

  const handleNavigate = useCallback((item: LibraryTreeItem) => {
    if (item.node_type === 'program') {
      if (item.program_id) {
        setSelectedProgramId(item.program_id);
      }
      return;
    }
    setRootId(item.id);
    setBreadcrumbs((prev) => [...prev, { id: item.id, name: item.name }]);
    setSearchInput('');
    setDebouncedSearch('');
  }, []);

  const handleBreadcrumbClick = useCallback((index: number) => {
    setBreadcrumbs((prev) => {
      const next = prev.slice(0, index + 1);
      const target = next[next.length - 1];
      setRootId(target.id);
      return next;
    });
    setSearchInput('');
    setDebouncedSearch('');
  }, []);

  const programKindOptions = useMemo(
    () =>
      programKindValues.map((v) => ({
        label: programKindLabels[v],
        value: v,
      })),
    [],
  );

  const recurrenceOptions = useMemo(
    () =>
      recurrenceModelValues.map((v) => ({
        label: recurrenceModelLabels[v],
        value: v,
      })),
    [],
  );

  const selectMenuIcon = useMemo(() => <CheckOutlined style={{ color: token.colorPrimary }} />, [token.colorPrimary]);

  const renderListDescription = (item: LibraryTreeItem) => {
    const parts: string[] = [];
    if (item.program_kind) {
      parts.push(programKindLabels[item.program_kind as ProgramKind] ?? item.program_kind);
    }
    if (item.recurrence_facet) {
      parts.push(recurrenceModelLabels[item.recurrence_facet as RecurrenceModel] ?? item.recurrence_facet);
    }
    if (item.is_level_based_facet) {
      parts.push('Уровневое');
    }
    const meta = parts.length > 0 ? parts.join(' · ') : null;

    return (
      <Space size="small">
        {meta ? <Text type="secondary">{meta}</Text> : null}
        {item.node_type === 'direction' && item.has_children ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            →
          </Text>
        ) : null}
      </Space>
    );
  };

  return (
    <div className={s.root}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space wrap size="middle">
          <SearchInput
            placeholder="Поиск по названию программы..."
            allowClear
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ width: 320 }}
          />
          <Select
            mode="multiple"
            placeholder="Вид обучения"
            options={programKindOptions}
            value={programKindFilter}
            onChange={setProgramKindFilter}
            style={{ minWidth: 200 }}
            allowClear
            maxTagCount={0}
            maxTagPlaceholder={joinMultiSelectDisplay}
            popupClassName={s['root-select-dropdown']}
            menuItemSelectedIcon={selectMenuIcon}
          />
          <Select
            mode="multiple"
            placeholder="Периодичность"
            options={recurrenceOptions}
            value={recurrenceFilter}
            onChange={setRecurrenceFilter}
            style={{ minWidth: 160 }}
            allowClear
            maxTagCount={0}
            maxTagPlaceholder={joinMultiSelectDisplay}
            popupClassName={s['root-select-dropdown']}
            menuItemSelectedIcon={selectMenuIcon}
          />
          <Select
            placeholder="Механика"
            value={levelBasedFilter === undefined ? undefined : levelBasedFilter ? 'level' : 'standard'}
            onChange={(v) => setLevelBasedFilter(v === 'level' ? true : v === 'standard' ? false : undefined)}
            allowClear
            style={{ minWidth: 160 }}
            options={[
              { label: 'Уровневое', value: 'level' },
              { label: 'Стандартное', value: 'standard' },
            ]}
          />
        </Space>

        <Breadcrumb>
          {breadcrumbs.map((crumb, idx) => (
            <Breadcrumb.Item key={crumb.id ?? 'root'}>
              {idx === breadcrumbs.length - 1 ? (
                <Text strong>{idx === 0 ? <HomeOutlined /> : crumb.name}</Text>
              ) : (
                <a onClick={() => handleBreadcrumbClick(idx)}>{idx === 0 ? <HomeOutlined /> : crumb.name}</a>
              )}
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>

        {listAwaitingFirstPaint ? (
          <PageSpinner size="default" />
        ) : !items || items.length === 0 ? (
          <Empty description="Нет элементов" />
        ) : (
          <div style={{ opacity: isFetching ? 0.65 : 1, transition: 'opacity 0.15s' }}>
            <List
              dataSource={items}
              renderItem={(item) => (
                <List.Item className={s['root-list-item']} onClick={() => handleNavigate(item)}>
                  <List.Item.Meta
                    avatar={
                      item.node_type === 'direction' ? (
                        <FolderOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
                      ) : (
                        <FileTextOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
                      )
                    }
                    title={item.name}
                    description={renderListDescription(item)}
                  />
                </List.Item>
              )}
            />
          </div>
        )}
      </Space>

      <ProgramDetailDrawer programId={selectedProgramId} onClose={() => setSelectedProgramId(null)} />
    </div>
  );
}
